/**
 * validation-toast — buduje polski, konkretny opis bledu walidacji do toasta,
 * na bazie surowych `issues` Zoda z odpowiedzi VALIDATION_ERROR (patrz
 * `extractValidationIssues` w `lib/errors.ts`).
 *
 * Gdy blad dotyczy wiecej niz jednego pola na raz — toast opisuje TYLKO
 * pierwsze pole (w porzadku, w jakim Zod zwraca issues), z dopiskiem
 * "(i N innych)". Reszta blednych pol jest i tak widoczna przez czerwona
 * ramke (patrz `useFieldErrors` + `hasError`) — toast nie musi ich wszystkich
 * wymieniac.
 */

import type { TFunction } from 'i18next';

import {
  extractFieldErrors,
  extractValidationIssues,
  type ValidationIssueLike,
} from '@/lib/errors';

/**
 * Klucze `POST_BODY_SCHEMA`/`PATCH_BODY_SCHEMA` sa camelCase (np. `contractNumber`),
 * a metadane pol z `/system-modules/<slug>/fields` maja `slug` snake_case (seed —
 * `contract_number`). Bez konwersji dopasowanie po slugu trafia TYLKO przypadkiem,
 * dla pol jednowyrazowych (`title`, `status`) — dla kazdego innego pola etykieta
 * nigdy sie nie znajdzie i do usera leci surowy techniczny identyfikator.
 */
export function camelToSnake(field: string): string {
  return field.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
}

/**
 * Etykieta pola z katalogu metadanych (`useSystemModuleFields`/`fields` z silnika
 * dokumentow) — probuje dopasowac po slugu wprost, potem po konwersji camelCase→
 * snake_case. `null` gdy pole nie jest w katalogu wcale — caller (patrz
 * `buildValidationToastMessage`) wtedy NIE pokazuje surowego identyfikatora, tylko
 * wraca do generycznego toastu.
 */
export function resolveFieldLabel(
  fields: ReadonlyArray<{ slug: string; name: string }>,
  field: string,
): string | null {
  const snake = camelToSnake(field);
  const match = fields.find((f) => f.slug === field || f.slug === snake);
  return match?.name ?? null;
}

/**
 * Kontenery pol zlozonych (ext-pola, pozycje dokumentu, tabele dodatkowe, strona
 * umowy) — ich pierwszy segment `path` nie jest nazwa pola, tylko nazwa struktury
 * w schemacie. Dla `extValues` drugi segment JEST realnym slugiem pola dodatkowego
 * (juz snake_case, bo tak sa zapisane w katalogu pol — `resolveFieldLabel` i tak
 * proboje obu wariantow). Dla reszty (nie mamy sensownej etykiety per-wiersz/
 * per-strona) zwracamy `null` — `buildValidationToastMessage` wtedy pomija ten
 * issue zamiast pokazac np. "items: ..." albo "primaryParty: ...".
 */
function resolveFieldFromPath(path: ReadonlyArray<string | number>): string | null {
  const first = path[0];
  if (typeof first !== 'string' || !first) return null;
  if (first === 'extValues') {
    const second = path[1];
    return typeof second === 'string' && second ? second : null;
  }
  if (first === 'items' || first === 'additionalTables' || first === 'primaryParty') {
    return null;
  }
  return first;
}

function describeIssue(issue: ValidationIssueLike, t: TFunction): string {
  switch (issue.code) {
    case 'too_big':
      return issue.type === 'number'
        ? t('common.validationIssue.tooBigNumber', { max: issue.maximum })
        : t('common.validationIssue.tooBigString', { max: issue.maximum });
    case 'too_small':
      // Wzorzec `z.string().trim().min(1)` dominuje w tych schematach — to jest
      // "pole wymagane", nie "za malo znakow". Bez tego rozroznienia najczestszy
      // w praktyce przypadek (puste wymagane pole) dawal blednie odmieniony i
      // mylacy komunikat "wymagane co najmniej 1 znakow".
      if (issue.type === 'string' && issue.minimum === 1) {
        return t('common.validationIssue.required');
      }
      return issue.type === 'number'
        ? t('common.validationIssue.tooSmallNumber', { min: issue.minimum })
        : t('common.validationIssue.tooSmallString', { min: issue.minimum });
    // Kody Zoda 4 obok dawnych z Zoda 3: invalid_format ← invalid_string,
    // invalid_value ← invalid_enum_value.
    case 'invalid_format':
    case 'invalid_string':
      return t('common.validationIssue.invalidFormat');
    case 'invalid_type':
      return t('common.validationIssue.invalidType');
    case 'invalid_value':
    case 'invalid_enum_value':
      return t('common.validationIssue.invalidEnumValue');
    default:
      return t('common.validationIssue.generic');
  }
}

/**
 * Zwraca "{etykieta pola}: {powod}" dla pierwszego pola z bledem (i sensowna
 * etykieta), z dopiskiem "(i N innych)" gdy blednych pol z etykieta jest wiecej.
 * `null` gdy: odpowiedz nie ma `issues` (inny kod bledu niz VALIDATION_ERROR),
 * ZADEN issue nie ma rozpoznawalnego pola (kontenery zlozone bez uzytecznej
 * etykiety — patrz `resolveFieldFromPath`), albo `getFieldLabel` nie zna pola —
 * we wszystkich tych przypadkach caller robi fallback na generyczny toast jak
 * dotychczas, ZAMIAST pokazac surowy techniczny identyfikator.
 */
/**
 * Mapa `pole (camelCase, jak w `details`/`useFieldErrors`) -> polski powod bledu`
 * — do pokazania POD polem formularza (czerwony tekst w `Field`). Reuzywa
 * `describeIssue`, wiec komunikaty sa identyczne z toastem. Kluczem jest
 * `path[0]` (ten sam, ktorym `applyServerError` indeksuje `details`), a nie
 * `resolveFieldFromPath` — pod polem chcemy trafic dokladnie w input, ktory
 * renderer podswietla przez `invalid('camelCaseKey')`. Pierwszy issue na pole
 * wygrywa (parytet z toastem). Puste, gdy odpowiedz nie ma `issues`.
 */
export function buildFieldIssueMessages(err: unknown, t: TFunction): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of extractValidationIssues(err)) {
    const field = issue.path[0];
    if (typeof field !== 'string' || !field || field in out) continue;
    // `custom` (Zod refine/superRefine) nie ma sensownego mapowania po stronie
    // klienta — jego znaczenie niesie WYŁĄCZNIE komunikat serwera z `details`
    // (np. reguła kwoty/kursu). Pomijamy go tutaj, żeby nie przykryć tego komunikatu
    // generykiem — callsite (translate) zrobi fallback na `details[0]`.
    if (issue.code === 'custom') continue;
    out[field] = describeIssue(issue, t);
  }
  return out;
}

export function buildValidationToastMessage(
  err: unknown,
  getFieldLabel: (field: string) => string | null,
  t: TFunction,
): string | null {
  const issues = extractValidationIssues(err);
  if (issues.length === 0) return null;

  const byField = new Map<string, { issue: ValidationIssueLike; label: string }>();
  for (const issue of issues) {
    const field = resolveFieldFromPath(issue.path);
    if (!field || byField.has(field)) continue;
    const label = getFieldLabel(field);
    if (label == null) continue;
    byField.set(field, { issue, label });
  }
  const resolved = Array.from(byField.values());
  if (resolved.length === 0) return null;

  const first = resolved[0] as { issue: ValidationIssueLike; label: string };
  const base = t('common.validationIssue.toastTemplate', {
    field: first.label,
    reason: describeIssue(first.issue, t),
  });
  if (resolved.length === 1) return base;
  return t('common.validationIssue.toastOthers', { base, count: resolved.length - 1 });
}

/**
 * Opis do toasta „Sprawdź wymagane pola": wymienia po etykiecie pola, których brakuje
 * (`Uzupełnij wymagane pola: A, B`). Klucze bez etykiety (`getFieldLabel` → `null`) są
 * pomijane — nigdy nie pokazujemy surowego identyfikatora technicznego. `undefined` gdy
 * żaden klucz nie ma etykiety (toast zostaje sam z tytułem).
 */
export function buildMissingFieldsDescription(
  keys: Iterable<string>,
  getFieldLabel: (key: string) => string | null,
  t: TFunction,
): string | undefined {
  const labels: string[] = [];
  for (const key of keys) {
    const label = getFieldLabel(key);
    if (label != null && !labels.includes(label)) labels.push(label);
  }
  if (labels.length === 0) return undefined;
  return t('tenant.documents.toast.missingRequiredFields', { fields: labels.join(', ') });
}

/**
 * Etykieta pola dokumentu w toastach walidacji: pole dodatkowe = nazwa z katalogu pól,
 * rdzeniowe = klucz i18n renderera (`tenant.documents.shared.fields.<klucz>`), o ile istnieje.
 * `null` gdy żadne z nich — caller nie pokazuje wtedy surowego identyfikatora.
 * `exists` = `i18n.exists` (wstrzykiwane, żeby helper pozostał czysty).
 */
export function resolveDocumentFieldLabel(
  fields: ReadonlyArray<{ slug: string; name: string; isCore: boolean }>,
  key: string,
  t: TFunction,
  exists: (i18nKey: string) => boolean,
): string | null {
  const ext = fields.find((f) => !f.isCore && f.slug === key);
  if (ext) return ext.name;
  const coreKey = `tenant.documents.shared.fields.${key}`;
  return exists(coreKey) ? t(coreKey) : null;
}

/**
 * Opis do toasta po odpowiedzi serwera `VALIDATION_ERROR` dla pól DODATKOWYCH: polskie
 * komunikaty z `details` (np. `Pole "Numer sprawy" jest wymagane`, patrz `require-ext.ts`),
 * po jednym na pole, rozdzielone średnikiem. Bierzemy tylko pola z katalogu, które nie są
 * rdzeniowe — komunikaty pól rdzeniowych bywają domyślnym angielskim Zoda i nie mogą
 * przeciekać do usera. `undefined` gdy brak takich komunikatów.
 */
export function buildExtFieldServerMessages(
  err: unknown,
  fields: ReadonlyArray<{ slug: string; isCore: boolean }>,
): string | undefined {
  const extSlugs = new Set(fields.filter((f) => !f.isCore).map((f) => f.slug));
  const messages: string[] = [];
  for (const [field, fieldMessages] of Object.entries(extractFieldErrors(err))) {
    const first = fieldMessages[0];
    if (extSlugs.has(field) && first !== undefined) messages.push(first);
  }
  return messages.length > 0 ? messages.join('; ') : undefined;
}
